import React, { useState, useEffect } from 'react';
import PageContainer from '../../components/common/PageContainer';
import PageSection from '../../components/common/PageSection';
import AdminNavigation from '../../components/admin/AdminNavigation';
import { useAuth } from '../../context/shared/AuthContext';
import apiService from '../../services/shared/apiService';

interface GiftRequest {
  id: string;
  userId: string;
  incomeRange: string;
  hardship?: string;
  reflectionStreak: number;
  tier: string;
  tokensAwarded: number;
  status: 'pending' | 'approved' | 'denied';
  createdAt: string;
  escalationFlag?: boolean;
}

const GiftBatchReviewPage = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState<GiftRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRequests, setSelectedRequests] = useState<Set<string>>(new Set());
  const [batchAction, setBatchAction] = useState<'approve' | 'deny' | ''>('');
  const [batchNote, setBatchNote] = useState('');

  useEffect(() => {
    // Only allow admin users
    if (user && user.role === 'admin') {
      fetchPendingRequests();
    }
  }, [user]);

  const fetchPendingRequests = async () => {
    try {
      setLoading(true);
      const response = await apiService.getPendingGiftRequests();
      setRequests(response.requests || []);
    } catch (err: any) {
      setError('Failed to fetch pending requests');
      console.error('Error fetching requests:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedRequests.size === requests.length) {
      setSelectedRequests(new Set());
    } else {
      setSelectedRequests(new Set(requests.map(r => r.id)));
    }
  };

  const handleSelectRequest = (requestId: string) => {
    const newSelected = new Set(selectedRequests);
    if (newSelected.has(requestId)) {
      newSelected.delete(requestId);
    } else {
      newSelected.add(requestId);
    }
    setSelectedRequests(newSelected);
  };

  const handleBatchAction = async () => {
    if (!batchAction || selectedRequests.size === 0) {
      return;
    }

    try {
      await apiService.batchReviewRequests({
        requestIds: Array.from(selectedRequests),
        action: batchAction,
        note: batchNote
      });
      
      setSelectedRequests(new Set());
      setBatchAction('');
      setBatchNote('');
      fetchPendingRequests(); // Refresh the list
    } catch (err: any) {
      setError(`Failed to ${batchAction} requests`);
      console.error(`Error ${batchAction}ing requests:`, err);
    }
  };

  const getStatusBadge = (status: string, escalationFlag?: boolean) => {
    const baseClasses = 'px-2 py-1 text-xs font-medium rounded-full';
    
    if (escalationFlag) {
      return <span className={`${baseClasses} bg-red-100 text-red-800  `}>
        ⚠️ Escalated
      </span>;
    }
    
    switch (status) {
      case 'pending':
        return <span className={`${baseClasses} bg-yellow-100 text-yellow-800  `}>
          Pending
        </span>;
      case 'approved':
        return <span className={`${baseClasses} bg-green-100 text-green-800  `}>
          Approved
        </span>;
      case 'denied':
        return <span className={`${baseClasses} bg-red-100 text-red-800  `}>
          Denied
        </span>;
      default:
        return <span className={`${baseClasses} bg-gray-100 text-gray-800  `}>
          {status}
        </span>;
    }
  };

  const getTierBadge = (tier: string) => {
    const baseClasses = 'px-2 py-1 text-xs font-medium rounded-full';
    
    switch (tier) {
      case 'High Need':
        return <span className={`${baseClasses} bg-purple-100 text-purple-800  `}>
          High Need
        </span>;
      case 'Medium Need':
        return <span className={`${baseClasses} bg-blue-100 text-blue-800  `}>
          Medium Need
        </span>;
      case 'Low Need':
        return <span className={`${baseClasses} bg-green-100 text-green-800  `}>
          Low Need
        </span>;
      case 'Ineligible':
        return <span className={`${baseClasses} bg-gray-100 text-gray-800  `}>
          Ineligible
        </span>;
      default:
        return <span className={`${baseClasses} bg-gray-100 text-gray-800  `}>
          {tier}
        </span>;
    }
  };

  // Check for admin role
  if (!user || user.role !== 'admin') {
    return (
      <PageContainer>
        <PageSection title="Access Denied" emoji="🚫">
          <p>You must be an authorized administrator to view this page.</p>
        </PageSection>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <AdminNavigation />
      <PageSection title="Batch Gift Request Review" emoji="📋">
        <div className="space-y-6">
          {error && (
            <div className="bg-red-50 /20 p-4 rounded-lg">
              <p className="text-red-800 ">{error}</p>
            </div>
          )}

          {/* Batch Actions */}
          {selectedRequests.size > 0 && (
            <div className="bg-blue-50 /20 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-800 ">
                    {selectedRequests.size} request{selectedRequests.size !== 1 ? 's' : ''} selected
                  </p>
                </div>
                <div className="flex items-center space-x-3">
                  <select
                    value={batchAction}
                    onChange={(e) => setBatchAction(e.target.value as 'approve' | 'deny' | '')}
                    className="px-3 py-2 border border-gray-300  rounded-lg bg-white  text-gray-900 "
                  >
                    <option value="">Select action</option>
                    <option value="approve">Approve All</option>
                    <option value="deny">Deny All</option>
                  </select>
                  <button
                    onClick={handleBatchAction}
                    disabled={!batchAction}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Apply
                  </button>
                </div>
              </div>
              
              {batchAction && (
                <div className="mt-3">
                  <label className="block text-sm font-medium mb-2">Batch Note (Optional)</label>
                  <textarea
                    value={batchNote}
                    onChange={(e) => setBatchNote(e.target.value)}
                    className="w-full p-3 border border-gray-300  rounded-lg bg-white  text-gray-900 "
                    rows={2}
                    placeholder="Add a note for all selected requests..."
                  />
                </div>
              )}
            </div>
          )}

          {loading ? (
            <div className="text-center py-8">
              <p>Loading pending requests...</p>
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600 ">No pending gift requests to review.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Select All */}
              <div className="flex items-center space-x-3 p-3 bg-gray-50  rounded-lg">
                <input
                  type="checkbox"
                  checked={selectedRequests.size === requests.length}
                  onChange={handleSelectAll}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm font-medium">
                  Select All ({requests.length} requests)
                </span>
              </div>

              {/* Requests List */}
              {requests.map((request) => (
                <div
                  key={request.id}
                  className={`border border-gray-200  rounded-lg p-4 transition-colors ${
                    selectedRequests.has(request.id) 
                      ? 'bg-blue-50 /20 border-blue-300 ' 
                      : 'hover:bg-gray-50 '
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      checked={selectedRequests.has(request.id)}
                      onChange={() => handleSelectRequest(request.id)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
                    />
                    
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <span className="text-sm font-medium text-gray-900 ">
                            User: {request.userId.substring(0, 8)}...
                          </span>
                          {getStatusBadge(request.status, request.escalationFlag)}
                          {getTierBadge(request.tier)}
                        </div>
                        <span className="text-sm text-gray-500 ">
                          {new Date(request.createdAt).toLocaleDateString()}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600 ">Income:</span>
                          <span className="ml-2 text-gray-900 ">
                            {request.incomeRange.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600 ">Hardship:</span>
                          <span className="ml-2 text-gray-900 ">
                            {request.hardship ? request.hardship.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'None'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600 ">Streak:</span>
                          <span className="ml-2 text-gray-900 ">
                            {request.reflectionStreak} days
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600 ">Tokens:</span>
                          <span className="ml-2 text-gray-900 ">
                            {request.tokensAwarded.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </PageSection>
    </PageContainer>
  );
};

export default GiftBatchReviewPage; 